import { WalkModeRenderOrders } from "@/modes/walk-mode/walk-mode-render-orders";
import { LineMaterial } from "@faro-lotv/lotv";
import type { BoundingRegion } from "@faro-lotv/service-wires";
import { useLotvDispose } from "@faro-lotv/spatial-ui";
import { useThree } from "@react-three/fiber";
import { useEffect, useMemo, type JSX, type Ref } from "react";
import { Color, DoubleSide, Mesh, MeshBasicMaterial } from "three";
import { LineSegments2, LineSegmentsGeometry } from "three-stdlib";
import {
  boundingRegionsToOutlineSegments,
  boundingRegionsToSphereGeometry,
} from "./bounding-region-geometry";

/** Kept low so the panorama stays readable where many findings overlap. */
const FILL_OPACITY = 0.15;

/** Thickness of the outline, in pixels. */
const OUTLINE_WIDTH = 2;

type BoundingRegionMeshProps = {
  /** The regions outlining the finding. */
  regions: BoundingRegion[];

  /** The color of the finding. */
  color: string;

  /** Optional ref to the filled mesh, e.g. to capture a screenshot of it. */
  meshRef?: Ref<Mesh>;
};

/**
 * @returns the overlay of a single AI finding, a semi-transparent area with a solid
 * outline, both rendered on the surface of the unit sphere.
 */
export function BoundingRegionMesh({
  regions,
  color,
  meshRef,
}: BoundingRegionMeshProps): JSX.Element {
  const { area, outline } = useMemo(() => {
    const area = new Mesh(
      boundingRegionsToSphereGeometry(regions),
      new MeshBasicMaterial({
        color,
        side: DoubleSide,
        transparent: true,
        opacity: FILL_OPACITY,
        // All the findings lie on the same sphere radius, so comparing their depth
        // makes the overlapping ones flicker. Their render order decides instead.
        depthTest: false,
        depthWrite: false,
      }),
    );
    area.renderOrder = WalkModeRenderOrders.AiBoundingBoxes;

    const outlineGeometry = new LineSegmentsGeometry();
    outlineGeometry.setPositions(boundingRegionsToOutlineSegments(regions));

    const outline = new LineSegments2(
      outlineGeometry,
      new LineMaterial({
        color: new Color(color).getHex(),
        linewidth: OUTLINE_WIDTH,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        worldUnits: false,
      }),
    );
    outline.renderOrder = WalkModeRenderOrders.AiBoundingBoxOutlines;

    // Part of the area, so that whoever shows or hides the finding gets its outline too.
    area.add(outline);

    return { area, outline };
  }, [regions, color]);

  useLotvDispose(area);

  // The outline is sized in pixels, so it needs to know how big the canvas is.
  const size = useThree((state) => state.size);
  useEffect(() => {
    outline.material.resolution.set(size.width, size.height);
  }, [outline, size]);

  return <primitive object={area} ref={meshRef} />;
}
